import { useState, useEffect } from 'react';
import { Row, Button, Card, Form, InputGroup, Table } from 'react-bootstrap';
import saveAs from 'file-saver';
import { useAppDispatch } from '../../Redux/Store';
import { getToastContent } from '../../utils/ToastContent';
import FinancialRoutes from '../../utils/constants/FinancialApp/FinancialRoutes';
import { toastMessage } from '../../Redux/Reducer/common';
import { IFeedBillOwnerDataStats } from '../../utils/Interface/FinancialApp/LotInterface';
import { useOwnerContext } from '../../Context/OwnerContext/OwnerContext';
import { apiCall_v2 } from '../../Services/AxiosService';
import Constants from '../../utils/Constants';
import { FeedBillConstants } from '../../apps/financeManager/modules/feedbill/constants/FeedBillConstants';
import { LotConstants } from '../../utils/constants/LotConstants';
import ErrorModal from '../common/ErrorModal';

const FeedBillOwnerData = () => {
    const { selectedOwner } = useOwnerContext();
    const [feedBillOwnerData, setFeedBillOwnerData] = useState<
        IFeedBillOwnerDataStats[]
    >([]);
    const [dateRange, setDateRange] = useState({
        startDate: '',
        endDate: '',
    });
    const [reportGenerated, setReportGenerated] = useState(false);
    const [errorModalDetail, setErrorModalDetail] = useState({
        show: false,
        message: '' as string | JSX.Element,
    });
    const [urls, setUrls] = useState({
        baseUrl: `${FinancialRoutes.Api.FEED_BILL_OWNER_DATA}`,
        reportBaseUrl: `${FinancialRoutes.Api.FEED_BILL_OWNER_DATA_EXCEL}`,
    });

    const handleStartDateChange = e => {
        setDateRange(prev => ({ ...prev, startDate: e.target.value }));
    };
    const handleEndDateChange = e => {
        setDateRange(prev => ({ ...prev, endDate: e.target.value }));
    };
    const closeErrorModal = () => {
        setErrorModalDetail(prev => ({
            ...prev,
            show: false,
        }));
    };

    const dispatch = useAppDispatch();
    const fetchFeedBillOwnerData = async () => {
        if (dateRange.startDate && dateRange.endDate && selectedOwner.name) {
            const response = await apiCall_v2({
                method: 'get',
                url: urls.baseUrl,
                isResRequired: true,
            });
            if (
                response.status >= Constants.responseCode.SUCCESS &&
                response.status < Constants.responseCode.MULTIPLE_CHOICES
            ) {
                setFeedBillOwnerData(response.data);
                setReportGenerated(true);
            } else {
                dispatch(toastMessage(getToastContent(response.status)));
            }
        } else {
            setErrorModalDetail({
                show: true,
                message: FeedBillConstants.dateSelectionErrorMessage,
            });
        }
    };
    const downloadFeedBillOwnerData = async () => {
        const response = await apiCall_v2({
            method: 'get',
            url: urls.reportBaseUrl,
            isResRequired: true,
            isResponseTypeBlob: true,
        });
        if (
            response.status >= Constants.responseCode.SUCCESS &&
            response.status < Constants.responseCode.MULTIPLE_CHOICES
        ) {
            const blob = new Blob([response.data], {
                type: 'application/octet-stream',
            });

            const fileName = `FeedBillOwnerData_${selectedOwner.name}_${dateRange.startDate}_${dateRange.endDate}.xlsx`;

            saveAs(blob, fileName);
        } else {
            dispatch(toastMessage(getToastContent(response.status)));
        }
    };

    useEffect(() => {
        if (
            selectedOwner.id !== '' &&
            dateRange.startDate !== '' &&
            dateRange.endDate !== ''
        ) {
            const customerIdQueryParam = `customerId=${selectedOwner.id}`;
            const startDateQueryParam = `startDate=${dateRange.startDate}`;
            const endDateQueryParam = `endDate=${dateRange.endDate}`;
            const apiUrl = `${FinancialRoutes.Api.FEED_BILL_OWNER_DATA}?${customerIdQueryParam}&${startDateQueryParam}&${endDateQueryParam}`;
            const apiUrlReport = `${FinancialRoutes.Api.FEED_BILL_OWNER_DATA_EXCEL}?${customerIdQueryParam}&${startDateQueryParam}&${endDateQueryParam}`;
            setUrls(prev => ({
                ...prev,
                baseUrl: apiUrl,
            }));
            setUrls(prev => ({
                ...prev,
                reportBaseUrl: apiUrlReport,
            }));
        }
    }, [selectedOwner, dateRange.startDate, dateRange.endDate]);
    useEffect(() => {
        setDateRange({
            startDate: '',
            endDate: '',
        });
        setReportGenerated(false);
        setFeedBillOwnerData([]);
    }, [selectedOwner.id]);
    useEffect(() => {
        if (dateRange.startDate === '' || dateRange.endDate === '') {
            setReportGenerated(false);
            setFeedBillOwnerData([]);
        }
    }, [dateRange.startDate, dateRange.endDate]);
    return (
        <div className="bg-light mt-5">
            <h3 className="text-center mt-3">
                {FeedBillConstants.report.title}
            </h3>
            <div className="row mt-3 justify-content-end">
                <div className="col-12">
                    {!selectedOwner.id ? (
                        <div className="text-center">
                            {FeedBillConstants.report.selectOwner}
                        </div>
                    ) : (
                        <>
                            <Row className="mb-3 justify-content-center">
                                <div className="col-4">
                                    <InputGroup data-size="md">
                                        <InputGroup.Text>
                                            {FeedBillConstants.report.dateFrom}
                                        </InputGroup.Text>
                                        <Form.Control
                                            type="date"
                                            value={dateRange.startDate}
                                            onChange={handleStartDateChange}
                                            max={
                                                dateRange.endDate ||
                                                '9999-12-31'
                                            }
                                        />
                                    </InputGroup>
                                </div>
                                <div className="col-4">
                                    <InputGroup data-size="md">
                                        <InputGroup.Text>
                                            {FeedBillConstants.report.dateTo}
                                        </InputGroup.Text>
                                        <Form.Control
                                            type="date"
                                            value={dateRange.endDate}
                                            onChange={handleEndDateChange}
                                            min={
                                                dateRange.startDate ||
                                                '0000-01-01'
                                            }
                                            max="9999-12-31"
                                        />
                                    </InputGroup>
                                </div>
                            </Row>
                            <Card className="shadow">
                                <Card.Header>
                                    <Row className="justify-content-between">
                                        <div className="col-6">
                                            {selectedOwner.name}
                                        </div>
                                        <Button
                                            onClick={fetchFeedBillOwnerData}
                                            className="btn-sm"
                                            variant="secondary"
                                        >
                                            {
                                                FeedBillConstants.report.button
                                                    .generateReport
                                            }
                                        </Button>
                                        {errorModalDetail && (
                                            <ErrorModal
                                                showModal={
                                                    errorModalDetail.show
                                                }
                                                closeModalHandler={
                                                    closeErrorModal
                                                }
                                                errorMessage={
                                                    errorModalDetail.message
                                                }
                                            />
                                        )}
                                    </Row>
                                </Card.Header>
                            </Card>
                            <div></div>
                            <div>
                                <Table striped hover bordered size="sm">
                                    <thead>
                                        <tr className="text-center">
                                            {LotConstants.reports.FEED_BILL_OWNER_DATA_TABLE_HEADERS.map(
                                                header => (
                                                    <th key={header.key}>
                                                        {header.label}
                                                    </th>
                                                ),
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {feedBillOwnerData.map((row, index) => (
                                            <tr key={index}>
                                                <td>{row.lotName}</td>
                                                <td className="text-right">
                                                    {row.headDaysOnFirstDate}
                                                </td>
                                                <td className="text-right">
                                                    {row.headDaysOnSecondDate}
                                                </td>
                                                <td className="text-right">
                                                    {row.headDaysDifference}
                                                </td>
                                                <td className="text-right">
                                                    {row.headCountOnFirstDate}
                                                </td>
                                                <td className="text-right">
                                                    {row.headCountOnSecondDate}
                                                </td>
                                                <td className="text-right">
                                                    {row.headCountDifference}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                                <div></div>
                                {reportGenerated ? (
                                    <Button
                                        onClick={downloadFeedBillOwnerData}
                                        className="mx-2 btn-sm"
                                        variant="secondary"
                                    >
                                        {
                                            FeedBillConstants.report.button
                                                .download
                                        }
                                    </Button>
                                ) : (
                                    <div></div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FeedBillOwnerData;
